import React from "react";
import * as R from "ramda";
import {
  takeEvery,
  all,
  fork,
  select,
  call,
  put,
  take
} from "redux-saga/effects";
import { actions, getters } from "./model";
import { eventId as getEventId } from "redux/modules/event/selectors";
import { getCredentials } from "redux/modules/user/selectors";
import { eventDetails as getEventDetails } from "redux/modules/event/selectors";
import { apiCall } from "App/Data/sagas";
import { registerError } from "redux/modules/errors/actions";
import { showModal } from "redux/modules/modal/actions";
import SelectDashboardFieldsModal from "./SelectDashboardFieldsModal/View";
import { makeFuture } from "utils/General/sagas";
import ModalWrapper from "components/Global/Modal/Wrappers/Black";
import * as STANDARD_MODULES from "@sessionboard/sessionboard-value-types/src/constants/standard-modules";
import { CUSTOM_FIELDS_DETAILS_FORM_ID } from "./constants";
import { getEditingField, getField, getFieldValue } from "./selectors";
import { showSnackbar } from "redux/modules/snackbar/actions";
import addValueApi from "redux/modules/modules/values/api";
import { getResult } from "ui-kit/FieldTypes/utils";
import { actions as FormActions } from "ui-kit/Form/model";

const getParams = function*() {
  const eventId = yield select(getEventId);
  const credentials = yield select(getCredentials);
  const eventDetails = yield select(getEventDetails);

  return { eventId, credentials, eventDetails };
};

const getCustomFields = function*({ preferences }) {
  try {
    const sessionId = yield select(getters.sessionId);
    const { payload } = yield call(apiCall, {
      method: "get",
      url: `/sessions/session/${sessionId}/custom-fields`,
      qs: {
        preferences
      }
    });

    yield put(actions.setCustomFields(payload));

    const { payload: translatedValuesPayload } = yield call(apiCall, {
      method: "get",
      url: `/sessions/session/${sessionId}/translated-values`,
      qs: {
        preferences
      }
    });

    yield put(actions.setTranslatedValues(translatedValuesPayload));
  } catch (err) {
    yield put(
      registerError([
        {
          system: err,
          user: "An error occurred getting fields"
        }
      ])
    );
  }
};

const init = function*({
  payload: { sessionId, sessionRecordId, refresh = false } = {}
}) {
  try {
    yield put(actions.setLoading(true));
    yield put(actions.setSessionId(sessionId));
    yield put(actions.setSessionRecordId(sessionRecordId));
    yield put(actions.setEditingFieldId(""));

    if (!refresh) {
      yield put(actions.setLoading(true));
    }

    yield call(getCustomFields, { preferences: {} });
  } finally {
    yield put(actions.setLoading(false));
  }
};

const showSelectFieldsModal = function*() {
  const { eventId } = yield call(getParams);
  const { default_preferences: preferences } = yield select(
    getters.customFields
  );
  const sessionId = yield select(getters.sessionId);

  const handleResult = makeFuture();
  yield put(
    showModal({
      content: (
        <SelectDashboardFieldsModal
          eventId={eventId}
          sessionId={sessionId}
          visibleFields={preferences.visible_fields}
          fieldOrder={preferences.field_order}
          onSave={finalPreferences => {
            handleResult.done({ type: "done", finalPreferences });
          }}
        />
      ),
      wrapper: ModalWrapper
    })
  );
  return yield call(handleResult.onRealized);
};

const handleSelectFieldsModal = function*() {
  const result = yield call(showSelectFieldsModal);
  if (result?.type === "done") {
    try {
      yield put(actions.setLoading(true));
      yield call(getCustomFields, {
        preferences: JSON.stringify(result.finalPreferences)
      });
    } finally {
      yield put(actions.setLoading(false));
    }
  }
};

const saveUpdatedValue = function*({ field, newField, oldValue = undefined }) {
  const sessionRecordId = yield select(getters.sessionRecordId);
  const { eventId, credentials } = yield call(getParams);
  const { id = undefined, type = undefined } = field;

  const { value: old = undefined } = oldValue || {};
  const value = getResult({ field, data: oldValue, newField });

  // handle: update value if new value is not like old value
  if (old !== value && value !== undefined) {
    yield call(addValueApi.post, credentials, {
      eventId,
      fieldId: id,
      recordId: sessionRecordId,
      moduleId: STANDARD_MODULES.sessions.id,
      value: {
        type,
        value
      }
    });

    // show snackbar
    yield put(showSnackbar({ message: "Profile updated" }));

    // refresh data w/ current preferences
    const { default_preferences: preferences } = yield select(
      getters.customFields
    );
    yield call(getCustomFields, {
      preferences: JSON.stringify(preferences)
    });
  } else {
    yield put(actions.setEditingFieldId(null));
  }

  return true;
};

const saveLanguageValues = function*() {
  const selectedTranslatedFieldId = yield select(
    getters.selectedTranslatedFieldId
  );
  const sessionRecordId = yield select(getters.sessionRecordId);
  const updatedLanguageValues = yield select(getters.updatedLanguageValues);
  const { eventId, credentials } = yield call(getParams);

  const updatedLanguageValuesForField =
    updatedLanguageValues[selectedTranslatedFieldId];

  if (updatedLanguageValuesForField) {
    const values = Object.keys(updatedLanguageValuesForField).reduce(
      (a, language) => {
        a.push({
          recordId: sessionRecordId,
          value: {
            type: "text",
            value: updatedLanguageValuesForField[language]
          },
          fieldId: selectedTranslatedFieldId,
          language
        });
        return a;
      },
      []
    );

    yield call(addValueApi.post, credentials, {
      eventId,
      fieldId: selectedTranslatedFieldId,
      moduleId: STANDARD_MODULES.sessions.id,
      bulk: true,
      values
    });
  }

  yield put(showSnackbar({ message: "Profile updated", action: "OK" }));

  yield put(actions.setUpdatedLanguageValues({}));

  // refresh data
  const { default_preferences: preferences } = yield select(
    getters.customFields
  );
  yield call(getCustomFields, {
    preferences: JSON.stringify(preferences)
  });

  return true;
};

const followFieldUpdate = function*({ fieldId }) {
  const newField = yield select(getEditingField, {
    instanceId: CUSTOM_FIELDS_DETAILS_FORM_ID,
    fieldId
  });

  const field = yield select(getField, { fieldId });
  const oldValue = yield select(getFieldValue, { fieldId });
  yield fork(saveUpdatedValue, { field, newField, oldValue });
};

const watchOnCloseEditor = function*() {
  for (;;) {
    const action = yield take([actions.onEdit.type]);
    const { payload: { id = null } = null } = action;
    if (action.type === actions.onEdit.type && id) {
      yield call(followFieldUpdate, {
        fieldId: id
      });
    }
  }
};

const watchOnInit = function*() {
  yield takeEvery(actions.init.type, init);
};
const watchShowSelectFieldsModal = function*() {
  yield takeEvery(actions.showSelectFieldsModal.type, handleSelectFieldsModal);
};
const watchSaveLanguageValues = function*() {
  yield takeEvery(actions.saveLanguageValues.type, saveLanguageValues);
};

const watchUpdateFields = function*() {
  for (;;) {
    const {
      meta: { fieldId, instanceId }
    } = yield take(FormActions.blurField.type);
    if (!R.isNil(fieldId) && instanceId === CUSTOM_FIELDS_DETAILS_FORM_ID) {
      yield call(followFieldUpdate, {
        fieldId
      });
    }
  }
};

const rootSaga = function*() {
  yield all([
    fork(watchOnInit),
    fork(watchShowSelectFieldsModal),
    fork(watchOnCloseEditor),
    fork(watchUpdateFields),
    fork(watchSaveLanguageValues)
  ]);
};

export default rootSaga;
